"use client";

import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
} from "@headlessui/react";
import { useEffect, useState, memo } from "react";
import clsx from "clsx";
import {
  ArrowDown01Icon,
  Logout01Icon,
  Menu01Icon,
  Menu02Icon,
  Message02Icon,
  Notification01Icon,
  RealEstate01Icon,
  Tick02Icon,
  UserCircleIcon,
  UserSettings01Icon,
  UserGroupIcon,
} from "hugeicons-react";
import Image from "next/image";
import Link from "next/link";
import { routes } from "@/lib/utils/routes";
import { useClientTranslations } from "@/lib/translate";
import Tooltip from "../../Tooltip";
import {
  useBusinessInfo,
  useManagedBusinesses,
} from "@/modules/hooks/business";
import {
  useSidebarStore,
  useAuthStore,
  useNotificationStore,
  useEmployeeStore,
} from "@/modules/store";
import { useRouter } from "next/navigation";
import { useUnreadConversations } from "@/modules/hooks/conversations/useUnreadConversations";
import { authService } from "@/modules/services/auth/authService";

export const LoadingHeader = () => {
  return (
    <header className="w-100 sticky top-0 backdrop-blur-md z-[1] border-b border-gray-200 dark:border-gray-800 px-4">
      <nav
        className={clsx(
          "mx-auto flex items-center justify-between",
          "h-[var(--layout-header-mobile-height-sm)] md:h-[var(--layout-header-mobile-height)] lg:h-[var(--layout-header-desktop-height)]",
        )}
      >
        <section className="flex flex-row gap-2 items-center">
          <div className="size-8 animate-pulse" />
        </section>
        <section className="flex flex-row gap-2 items-center animate-pulse">
          <div className="rounded-full bg-gray-200 dark:bg-gray-700 h-10 w-10"></div>
          <div className="rounded-full bg-gray-200 dark:bg-gray-700 h-10 w-10"></div>
          <div className="rounded-full bg-gray-200 dark:bg-gray-700 h-10 w-10"></div>
        </section>
      </nav>
    </header>
  );
};

const HeaderManager = memo(() => {
  const { managedBusinesses, selectedBusiness, onChangeBusiness } =
    useManagedBusinesses();
  const { toggleSidebarLeft, toggleSidebarRight } = useSidebarStore();
  const { logoutUser } = useAuthStore();
  const { employeeInfo, getEmployeeInfo, isLoadingEmployee } = useEmployeeStore();
  const { t, ready } = useClientTranslations("header-manager");
  const { isLoadingBusiness } = useBusinessInfo();
  const [isLoadingComplete, setIsLoadingComplete] = useState(false);
  const { totalUnreadConversations } = useUnreadConversations();
  const totalUnreadNotifications = useNotificationStore(
    (state) => state.notReadNoti.total,
  );
  const router = useRouter();

  useEffect(() => {
    if (ready && !isLoadingBusiness && !isLoadingEmployee) {
      setIsLoadingComplete(true);
    }
  }, [ready, isLoadingBusiness, isLoadingEmployee]);

  useEffect(() => {
    if (!employeeInfo && !isLoadingEmployee) {
      getEmployeeInfo();
    }
  }, [employeeInfo, isLoadingEmployee, getEmployeeInfo]);

  const handleLogout = () => {
    authService.logout();
    logoutUser();
    router.push(routes.login);
  };

  const handleMessages = () => {
    router.push(routes.messages);
  };

  return (
    <header className="w-100 sticky top-0 backdrop-blur-md z-[2] border-b border-gray-200 dark:border-gray-800 px-4">
      <nav
        className={clsx(
          "mx-auto flex items-center justify-between",
          "h-[var(--layout-header-mobile-height-sm)] md:h-[var(--layout-header-mobile-height)] lg:h-[var(--layout-header-desktop-height)]",
        )}
      >
        <section className="flex flex-row gap-2 items-center">
          <button
            className="size-8 lg:hidden cursor-pointer"
            onClick={() => toggleSidebarLeft()}
          >
            <Menu02Icon />
          </button>
          <Listbox value={selectedBusiness} onChange={onChangeBusiness}>
            <ListboxButton
              className={clsx(
                "w-36 relative z-[1] block rounded-lg bg-gray-100 dark:bg-white/5 py-1.5 pr-8 pl-3 text-left text-sm/6 text-gray-500 dark:text-white",
                "focus:outline-none data-[focus]:outline-2 data-[focus]:-outline-offset-2 data-[focus]:outline-white/25",
              )}
            >
              {selectedBusiness ? (
                <div className="flex items-center gap-2 truncate">
                  <Image
                    src={selectedBusiness?.companyLogo}
                    alt={selectedBusiness?.shortCompanyName}
                    width={20}
                    height={20}
                  />
                  {selectedBusiness?.shortCompanyName}
                </div>
              ) : (
                <div className="flex items-center gap-2 truncate">
                  <div className="w-[20px] h-[20px] rounded-md bg-gray-200 dark:bg-gray-700"></div>
                  <div className="h-[20px] w-full rounded-md bg-gray-200 dark:bg-gray-700"></div>
                </div>
              )}
              <ArrowDown01Icon
                className="group pointer-events-none absolute top-2.5 right-2.5 size-4 fill-white/60"
                aria-hidden="true"
              />
            </ListboxButton>
            <ListboxOptions
              anchor="bottom start"
              transition
              className={clsx(
                "z-[2] min-w-52 max-w-60 rounded-xl border backdrop-blur-3xl border-gray-300 dark:border-gray-700 bg-white/5 p-1",
                "[--anchor-gap:var(--spacing-1)] focus:outline-nonetransition duration-100 ease-in data-[leave]:data-[closed]:opacity-0",
              )}
            >
              {managedBusinesses.map((business) => (
                <ListboxOption
                  key={business.id}
                  value={business}
                  className="cursor-pointer group flex items-center justify-between gap-2 rounded-lg py-1.5 px-3 select-none hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <div className="flex items-center gap-2">
                    <Image
                      src={business.companyLogo}
                      alt={business.shortCompanyName}
                      width={20}
                      height={20}
                    />
                    <span className="text-sm/6 text-gray-500 dark:text-white group-data-[selected]:text-primary">
                      {business.shortCompanyName}
                    </span>
                  </div>
                  <Tick02Icon className="invisible size-4 fill-white group-data-[selected]:visible" />
                </ListboxOption>
              ))}
            </ListboxOptions>
          </Listbox>
        </section>
        {!isLoadingComplete ? (
          <section className="flex flex-row gap-2 items-center animate-pulse">
            <div className="rounded-full bg-gray-200 dark:bg-gray-700 h-10 w-10"></div>
            <div className="rounded-full bg-gray-200 dark:bg-gray-700 h-10 w-10"></div>
            <div className="rounded-full bg-gray-200 dark:bg-gray-700 h-10 w-10"></div>
          </section>
        ) : (
          <section className="flex flex-row gap-2 items-center">
            <div className="hidden lg:flex flex-row gap-2 items-center">
              <Tooltip content={t("messages")}>
                <button
                  className="rounded-full hover:bg-gray-300 dark:hover:bg-gray-700 p-1 relative"
                  onClick={handleMessages}
                >
                  {totalUnreadConversations > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                      {totalUnreadConversations}
                    </span>
                  )}
                  <div>
                    <Message02Icon />
                  </div>
                </button>
              </Tooltip>
              <Tooltip content={t("notifications")}>
                <button
                  className="rounded-full hover:bg-gray-300 dark:hover:bg-gray-700 p-1 relative"
                  onClick={() => toggleSidebarRight()}
                >
                  {totalUnreadNotifications > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                      {totalUnreadNotifications}
                    </span>
                  )}
                  <Notification01Icon />
                </button>
              </Tooltip>
            </div>
            <div className="block lg:hidden">
              <Menu>
                <MenuButton className="rounded-full hover:bg-gray-300 dark:hover:bg-gray-700 p-1">
                  <Menu01Icon />
                </MenuButton>
                <MenuItems
                  transition
                  anchor="bottom end"
                  className="w-52 z-[51] origin-top-right rounded-xl border backdrop-blur-3xl border-gray-300 dark:border-gray-700 bg-white/5 p-1 transition duration-100 ease-out [--anchor-gap:var(--spacing-1)] focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0"
                >
                  <MenuItem>
                    <Link
                      className="group flex w-full items-center gap-2 rounded-lg py-1.5 px-3 hover:bg-gray-100 dark:hover:bg-gray-800 relative"
                      href={routes.messages}
                    >
                      <Message02Icon />
                      {t("messages")}
                      {totalUnreadConversations > 0 && (
                        <span className="absolute top-1/2 right-1 -translate-y-1/2 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                          {totalUnreadConversations}
                        </span>
                      )}
                    </Link>
                  </MenuItem>
                  <MenuItem>
                    <button
                      className="group flex w-full items-center gap-2 rounded-lg py-1.5 px-3 hover:bg-gray-100 dark:hover:bg-gray-800 relative"
                      onClick={() => toggleSidebarRight()}
                    >
                      <Notification01Icon />
                      {t("notifications")}
                      {totalUnreadNotifications > 0 && (
                        <span className="absolute top-1/2 right-1 -translate-y-1/2 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                          {totalUnreadNotifications}
                        </span>
                      )}
                    </button>
                  </MenuItem>
                </MenuItems>
              </Menu>
            </div>
            <div>
              <Menu>
                <MenuButton className="inline-flex items-center gap-2 p-1 rounded-full focus:outline-none dark:data-[hover]:bg-gray-700 dark:data-[open]:bg-gray-700 dark:data-[focus]:outline-1 dark:data-[focus]:outline-white data-[hover]:bg-gray-200 data-[focus]:bg-gray-200 data-[open]:bg-gray-200">
                  {employeeInfo && employeeInfo.avatar ? (
                    <Image
                      src={employeeInfo.avatar}
                      alt="avatar"
                      className="rounded-full"
                      width={30}
                      height={30}
                    />
                  ) : (
                    <UserCircleIcon width={30} height={30} />
                  )}
                </MenuButton>
                <MenuItems
                  transition
                  anchor="bottom end"
                  className="w-64 z-[51] origin-top-right rounded-xl border backdrop-blur-3xl border-gray-300 dark:border-gray-700 bg-white/5 p-1 transition duration-100 ease-out [--anchor-gap:var(--spacing-1)] focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0"
                >
                  <MenuItem>
                    <Link href={routes.company.employeeInfo}>
                      <button className="group flex w-full items-center gap-2 rounded-lg py-1.5 px-3 hover:bg-gray-100 dark:hover:bg-gray-800">
                        <UserSettings01Icon />
                        {t("account_management")}
                      </button>
                    </Link>
                  </MenuItem>
                  <MenuItem>
                    <Link href={routes.company.profile}>
                      <button className="group flex w-full items-center gap-2 rounded-lg py-1.5 px-3 hover:bg-gray-100 dark:hover:bg-gray-800">
                        <RealEstate01Icon />
                        {t("company_profile")}
                      </button>
                    </Link>
                  </MenuItem>
                  <MenuItem>
                    <Link href={routes.company.employees}>
                      <button className="group flex w-full items-center gap-2 rounded-lg py-1.5 px-3 hover:bg-gray-100 dark:hover:bg-gray-800">
                        <UserGroupIcon />
                        {t("manage_employees")}
                      </button>
                    </Link>
                  </MenuItem>
                  <div className="my-1 h-px bg-black/5 dark:bg-white/5" />
                  <MenuItem>
                    <button
                      onClick={handleLogout}
                      className="group flex w-full items-center gap-2 rounded-lg py-1.5 px-3 hover:bg-gray-100 dark:hover:bg-gray-800"
                    >
                      <Logout01Icon />
                      {t("logout")}
                    </button>
                  </MenuItem>
                </MenuItems>
              </Menu>
            </div>
          </section>
        )}
      </nav>
    </header>
  );
});

HeaderManager.displayName = "HeaderManager";

export default HeaderManager;
